import { DynamicResolution, RefreshRateProbe, type DynamicResolutionOptions } from '../../render/DynamicResolution';
import type { System } from '../ServiceLocator';

export type RenderScaleMode = 'fixed' | 'auto';

export interface DrsTickResult {
  /** New scale emitted by DRS this frame; caller calls applyScale. */
  readonly newScale: number | null;
  /** True when DRS reached its floor AND smoothed frame time exceeds
   *  the slow threshold long enough to flag a severe budget violation
   *  (FRAME_CAP_FLOOR_FRAMES). */
  readonly floorStuckSevere: boolean;
  /** True when the floor-stuck slow streak hit the adaptive-quality
   *  reduction threshold (FLOOR_REDUCE_FRAMES). */
  readonly floorStuckReduce: boolean;
  readonly drsCurrent: number;
  readonly slowMs: number;
}

/**
 * Owns Dynamic Resolution Scaling: DynamicResolution instance,
 * RefreshRateProbe, mode (fixed/auto), manual scale, floor-stuck
 * book-keeping, and the EMA-smoothed frame time used to decide
 * severity.
 *
 * The applyScale side effect crosses system boundaries (it touches the
 * renderer's pixel ratio, PostFX bloom composer, the resize handler,
 * the tuning state). The system takes applyScale as an injected
 * callback so it can decide WHEN to apply without owning HOW.
 */
export class DrsSystem implements System {
  readonly name = 'DrsSystem';

  static readonly MIN_SCALE = 0.85;
  static readonly STORAGE_KEY = 'tst:drs:scale:v9';
  static readonly FRAMES_BEFORE_DOWN = 8;
  static readonly FRAME_CAP_FLOOR_FRAMES = 90;
  static readonly FLOOR_REDUCE_FRAMES = 45;
  static readonly SEVERE_SLOW_MULTIPLIER = 1.35;

  drs: DynamicResolution | null = null;
  refreshProbe: RefreshRateProbe | null = null;
  mode: RenderScaleMode = 'fixed';
  manualScale: number;
  detectedRefreshMs: number | null = null;
  floorSlowFrames = 0;
  floorSmoothedMs = 0;

  readonly nativeDpr: number;
  readonly drsMaxScale: number;
  readonly renderScaleMin: number;
  readonly renderScaleMax: number;
  readonly autoAvailable: boolean;

  private applyScale: (scale: number) => void = () => {};

  constructor(options: {
    initialFixedRenderScale: number;
    renderScaleMin: number;
    renderScaleMax: number;
    nativeDpr: number;
    drsMaxScale: number;
    drsExplicitlyOff: boolean;
    drsPersistenceEnabled: boolean;
    drsAutoEnabled: boolean;
    targetMs: number;
  }) {
    this.manualScale = options.initialFixedRenderScale;
    this.nativeDpr = options.nativeDpr;
    this.drsMaxScale = options.drsMaxScale;
    this.renderScaleMin = options.renderScaleMin;
    this.renderScaleMax = options.renderScaleMax;

    if (!options.drsExplicitlyOff) {
      const drsOptions: DynamicResolutionOptions = {
        maxScale: options.drsMaxScale,
        minScale: DrsSystem.MIN_SCALE,
        targetMs: options.targetMs,
        framesBeforeDown: DrsSystem.FRAMES_BEFORE_DOWN,
        ...(options.drsPersistenceEnabled
          ? { storageKey: DrsSystem.STORAGE_KEY }
          : { initialScale: 1 }),
      };
      this.drs = new DynamicResolution(drsOptions);
      this.refreshProbe = new RefreshRateProbe(60);
    }

    this.autoAvailable = this.drs !== null;
    this.mode = options.drsAutoEnabled && this.drs ? 'auto' : 'fixed';
  }

  /** Inject the side effect that actually applies a scale to the
   *  renderer + downstream subsystems. Called by bootstrap after the
   *  renderer is constructed and after PostFXSystem is registered. */
  setApplyScale(fn: (scale: number) => void): void {
    this.applyScale = fn;
  }

  /** Probe the display refresh rate (called per-frame until done).
   *  Returns the median ms if the probe just settled, else null. */
  tickRefreshProbe(now: number): number | null {
    if (!this.refreshProbe || this.refreshProbe.isDone()) return null;
    const median = this.refreshProbe.tick(now);
    if (median !== null) this.detectedRefreshMs = median;
    return median;
  }

  /** Per-frame DRS step. Returns the emitted scale + floor-stuck
   *  decisions for the caller to act on (e.g. frame-cap throttle,
   *  reduce render load). */
  tick(frameDt: number): DrsTickResult {
    if (!this.drs) {
      return { newScale: null, floorStuckSevere: false, floorStuckReduce: false, drsCurrent: this.manualScale, slowMs: 0 };
    }
    const newScale = this.drs.tick(frameDt * 1000);
    const thresholds = this.drs.thresholds();
    const atFloor = this.drs.current() <= DrsSystem.MIN_SCALE + 0.001;
    const frameMs = frameDt * 1000;
    this.floorSmoothedMs = this.floorSmoothedMs === 0
      ? frameMs
      : this.floorSmoothedMs * 0.9 + frameMs * 0.1;
    const stillSlow = this.floorSmoothedMs > thresholds.slowMs;
    const severe = this.floorSmoothedMs > thresholds.targetMs * DrsSystem.SEVERE_SLOW_MULTIPLIER;
    let floorStuckSevere = false;
    let floorStuckReduce = false;
    if (atFloor && stillSlow) {
      this.floorSlowFrames++;
      if (severe && this.floorSlowFrames >= DrsSystem.FRAME_CAP_FLOOR_FRAMES) floorStuckSevere = true;
      if (this.floorSlowFrames >= DrsSystem.FLOOR_REDUCE_FRAMES) floorStuckReduce = true;
    } else {
      this.floorSlowFrames = 0;
      if (!atFloor) this.floorSmoothedMs = 0;
    }
    return {
      newScale,
      floorStuckSevere,
      floorStuckReduce,
      drsCurrent: this.drs.current(),
      slowMs: thresholds.slowMs,
    };
  }

  /** Tell DRS the new target frame budget (used after refresh rate
   *  detection or after a frame cap change). */
  setTargetMs(targetMs: number): void {
    this.drs?.setTargetMs(targetMs);
  }

  setMode(next: RenderScaleMode): void {
    this.mode = next === 'auto' && this.drs ? 'auto' : 'fixed';
    this.floorSlowFrames = 0;
    this.floorSmoothedMs = 0;
    if (this.mode === 'auto' && this.drs) this.applyScale(this.drs.current());
    else this.applyScale(this.manualScale);
  }

  setScale(scale: number): void {
    this.manualScale = this.clampScale(scale);
    this.mode = 'fixed';
    this.applyScale(this.manualScale);
  }

  clampScale(scale: number): number {
    if (!Number.isFinite(scale) || scale <= 0) return this.manualScale;
    return Math.max(this.renderScaleMin, Math.min(this.renderScaleMax, scale));
  }

  current(): number {
    return this.mode === 'auto' ? this.drs?.current() ?? this.manualScale : this.manualScale;
  }

  /** Diagnostic snapshot for window.__twin / overlay. Shape matches the
   *  legacy fields that lived inline in bootstrap so spreading this into
   *  the diag object preserves the e2e contract. */
  snapshot(): { renderScale: Record<string, unknown>; drs: Record<string, unknown> } {
    return {
      renderScale: {
        mode: this.mode,
        current: this.current(),
        manual: this.manualScale,
        nativeDpr: this.nativeDpr,
        maxScale: this.renderScaleMax,
      },
      drs: this.drs
        ? {
          enabled: this.mode === 'auto',
          current: this.drs.current(),
          thresholds: this.drs.thresholds(),
          maxScale: this.drsMaxScale,
          floorSlowFrames: this.floorSlowFrames,
          floorSmoothedMs: this.floorSmoothedMs,
        }
        : { enabled: false },
    };
  }
}
